// Domain-wide VoiceFabric flow canvas at /admin/voicefabric/flow/:domainId.
// Shows every rule in the domain as a compact "ruleCluster" node. Edges are
// drawn between rules whose `branches[].next_rule_id` chains to another rule.
// Click a rule cluster to drill into the per-rule canvas (FlowCanvasPage).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ReactFlow, Background, Controls, MiniMap, ReactFlowProvider,
  applyNodeChanges, applyEdgeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, LayoutTemplate, Loader2, Network } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import AdminLayout from '../../AdminLayout';
import { NODE_TYPES } from './FlowNodes';
import { layoutWithDagre } from './dagreLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildDomainGraph = (rules) => {
  const ruleById = new Map(rules.map(r => [r.id, r]));
  const nodes = rules.map(r => ({
    id: `rule-${r.id}`,
    type: 'ruleCluster',
    position: { x: 0, y: 0 },
    data: {
      ruleId: r.id,
      label: r.name,
      disabled: r.enabled === false,
      branchCount: (r.branches || []).length,
      actionTypes: Array.from(new Set(
        (r.branches || []).map(b => b.action_type || 'log')
      )),
    },
  }));

  const edges = [];
  rules.forEach(r => {
    (r.branches || []).forEach(b => {
      const nrid = (b.next_rule_id || '').trim();
      if (nrid && ruleById.has(nrid)) {
        edges.push({
          id: `chain-${r.id}-${b.id}-${nrid}`,
          source: `rule-${r.id}`,
          target: `rule-${nrid}`,
          label: b.label || 'chain',
          labelStyle: { fill: '#fbbf24', fontSize: 11 },
          labelBgStyle: { fill: '#0A0F1C' },
          style: { stroke: '#fbbf24', strokeWidth: 1.5 },
          animated: true,
        });
      }
    });
  });

  return { nodes, edges };
};

const DomainFlowInner = () => {
  const { domainId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Stable references — prevents ReactFlow children from re-rendering on every parent update.
  const reactFlowProOptions = useMemo(() => ({ hideAttribution: true }), []);
  const miniMapStyle = useMemo(
    () => ({ background: '#0A0F1C', border: '1px solid rgba(255,255,255,0.05)' }),
    [],
  );

  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [domain, setDomain] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [rulesR, domainR] = await Promise.all([
          axios.get(`${API}/admin/domains/${domainId}/voicefabric/rules`, { headers }),
          axios.get(`${API}/admin/domains/${domainId}`, { headers }),
        ]);
        if (cancelled) return;
        setRules(rulesR.data.rules || []);
        setDomain(domainR.data || null);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load domain flow');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [domainId, headers]);

  useEffect(() => {
    const { nodes: rawNodes, edges: rawEdges } = buildDomainGraph(rules);
    setNodes(layoutWithDagre(rawNodes, rawEdges, 'LR'));
    setEdges(rawEdges);
  }, [rules]);

  const onNodesChange = useCallback(
    changes => setNodes(ns => applyNodeChanges(changes, ns)),
    []
  );
  const onEdgesChange = useCallback(
    changes => setEdges(es => applyEdgeChanges(changes, es)),
    []
  );
  const onNodeClick = (_e, node) => {
    if (node.type === 'ruleCluster' && node.data?.ruleId) {
      navigate(`/admin/voicefabric/flow/${domainId}/${node.data.ruleId}`);
    }
  };
  const reLayout = () => {
    const { nodes: rawNodes, edges: rawEdges } = buildDomainGraph(rules);
    setNodes(layoutWithDagre(rawNodes, rawEdges, 'LR'));
    setEdges(rawEdges);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-6 h-6 text-white/30 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const chainCount = edges.length;

  return (
    <AdminLayout>
      <div className="-mx-6 -my-6 h-[calc(100vh-64px)] flex flex-col bg-[#070B14]">
        <div className="flex items-center justify-between px-6 py-3 border-b border-white/5 bg-[#0A0F1C]">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/voicefabric')}
              className="text-white/40 hover:text-white p-1.5 rounded hover:bg-white/5"
              data-testid="domain-flow-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              <Network className="w-4 h-4 text-teal-400" />
              <div>
                <h1 className="text-white text-sm font-semibold">
                  {domain?.company?.name || domain?.yabbit_domain || 'Domain Flow'}
                </h1>
                <p className="text-white/40 text-xs">
                  {rules.length} rule{rules.length === 1 ? '' : 's'} · {chainCount} chain edge{chainCount === 1 ? '' : 's'}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={reLayout}
              className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10"
              data-testid="domain-flow-relayout-btn"
            >
              <LayoutTemplate className="w-3.5 h-3.5" /> Auto-arrange
            </button>
          </div>
        </div>

        <div className="flex-1 relative">
          {rules.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-white/40 text-sm">
              No rules in this domain yet — create one from the VoiceFabric page.
            </div>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={onNodeClick}
              nodeTypes={NODE_TYPES}
              fitView
              colorMode="dark"
              proOptions={reactFlowProOptions}
            >
              <Background color="#1f2937" gap={24} />
              <Controls className="!bg-[#0A0F1C] !border-white/10" />
              <MiniMap
                pannable zoomable
                nodeStrokeColor="#0d9488"
                nodeColor="#0E1422"
                maskColor="rgba(0,0,0,0.6)"
                style={miniMapStyle}
              />
            </ReactFlow>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

const DomainFlowPage = () => (
  <ReactFlowProvider>
    <DomainFlowInner />
  </ReactFlowProvider>
);

export default DomainFlowPage;
